import { CalendarToday, Circle, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Dialog, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { DiaMes, DiaMesProps } from "./diaCalendario";
import { RequestError, useApi } from "../../services";
import { Simulado } from "../../services/api/simulados";
import { Seminario } from "../../services/api/seminarios";
import { Treinamento } from "../../services/api/treinamentos";
import { SegurancaBarragem } from "../../services/api/segBarragem";
import { useBarragemContext, useUserContext } from "../../context";
import { ConfirmModal, ErrorOnDialog } from "../modals";
import { CardNovoEvento } from "./cardNovoEvento";
import { EventoBarragem } from "../../services/api/company";

interface CalendarioBarragemProps {
  minHeight?: number;
}
export const CalendarioBarragem: React.FC<CalendarioBarragemProps> = ({ minHeight }) => {
  const api = useApi();
  const user = useUserContext();
  const { barragem } = useBarragemContext();
  const { palette } = useTheme();

  const sizeRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [state, setState] = useState(false);

  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  const [days, setDays] = useState<DiaMesProps[]>([]);
  const [indexSemana, setIndexSemana] = useState(0); // Index da semana no calendário, quando 0 a semana atual é a segunda no calendário

  // Data para adicionar ou remover um novo evento
  const [addDate, setAddDate] = useState<Date | undefined>();
  const [editItem, setEditItem] = useState<EventoBarragem | undefined>();
  const [excludeItem, setExcludeItem] = useState<EventoBarragem>();

  const [error, setError] = useState<RequestError>();

  // Tamanho do calendário
  useEffect(() => {
    if (sizeRef.current) {
      const { width, height } = sizeRef.current.getBoundingClientRect();
      setDimensions({ width, height });
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setDimensions({ width, height });
        }
      });

      resizeObserver.observe(sizeRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // Tamanho do dia
  useEffect(() => {
    const semanas = 5; //Math.max(1, Math.floor(dimensions.height / 5));

    // Data atual
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Data do calendário
    const dataAtual = new Date(now);
    dataAtual.setDate(dataAtual.getDate() + (7 * indexSemana));
    const semanaAtual = getWeekRange(dataAtual);

    // Lista com as datas
    let daysShow: DiaMesProps[] = [];

    // Semana anterior
    if (semanas > 1) {
      const dia = new Date(semanaAtual[0]);
      dia.setDate(semanaAtual[0].getDate() - 1);

      const dadosSemana = processWeek(dia, now);
      daysShow = [...daysShow, ...dadosSemana];
    }

    // Semana atual
    const dadosSemana = processWeek(dataAtual, now);
    daysShow = [...daysShow, ...dadosSemana];

    // Semanas após a data atual
    if (semanas > 2) {
      for (let i = 0; i < semanas - 2; i++) {
        const dia = new Date(semanaAtual[6]);
        dia.setDate(semanaAtual[6].getDate() + (7 * i) + 1);

        const dadosSemana = processWeek(dia, now);
        daysShow = [...daysShow, ...dadosSemana];
      }
    }

    if (daysShow.length >= 3) {
      setStartDate(daysShow[1].data as Date);
      setEndDate(daysShow[daysShow.length - 1].data as Date);
    }

    setDays([...daysShow]);
  }, [state, dimensions, indexSemana]);

  // Eventos
  useEffect(() => {
    if (!barragem || "error" in barragem) return;
    if (!startDate || !endDate) return;

    api.company.getEventos(barragem.idEmpreendimento, startDate, endDate).then((eventos) => {
      if ("error" in eventos) {
        setError(eventos);
        return;
      }

      for (const evento of eventos) {
        for (const day of days) {
          if (typeof day.data === "string") continue;

          const dia = new Date(day.data);
          dia.setHours(0, 0, 0);
          const diaEvento = new Date(evento.evento.data);
          diaEvento.setHours(0, 0, 0);

          if (dia.getTime() !== diaEvento.getTime()) continue;

          if (evento.tipo === "segurancaBarragem") {
            day.segBarragem.push((evento.evento as SegurancaBarragem));
          } else if (evento.tipo === "seminario") {
            day.seminarios.push(evento.evento as Seminario);
          } else if (evento.tipo === "simulado") {
            day.simulados.push(evento.evento as Simulado);
          } else if (evento.tipo === "treinamento") {
            day.treinamentos.push(evento.evento as Treinamento);
          }

          break;
        }
      }

      setDays([...days]);

    });

  }, [startDate, endDate, barragem]);

  // Remover evento
  const handleRemove = (evento: EventoBarragem) => {
    setExcludeItem(evento);
  };
  const handleExclude = () => {
    if (!excludeItem) return;

    const tipos = {
      "seminario": api.seminarios.remove,
      "simulado": api.simulados.remove,
      "treinamento": api.treinamentos.remove,
      "segurancaBarragem": api.segBarragem.remove,
    };

    const func = tipos[excludeItem.tipo];

    func(excludeItem.evento.id).then(val => {
      if ("error" in val) {
        setError(val);
        return;
      }
      setState(!state);
    });

    setExcludeItem(undefined);
  };

  // Adicionar evento
  const handleAddEvent = (data: Date) => {
    if (user && !('error' in user) && user.admin) {
      setAddDate(data);
    }
  };

  // Editar evento
  const handleChangeEvent = (evento: EventoBarragem) => {
    setAddDate(evento.evento.data);
    setEditItem(evento);
  };

  const handleAdd = (item: EventoBarragem, file?: File) => {
    if (!barragem || "error" in barragem) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let func: any = undefined;

    if (item.tipo === "segurancaBarragem") {
      func = api.segBarragem;
    } else if (item.tipo === "seminario") {
      func = api.seminarios;
    } else if (item.tipo === "simulado") {
      func = api.simulados;
    } else if (item.tipo === "treinamento") {
      func = api.treinamentos;
    }

    if (!func) return;

    if (item.evento.id > 0) {
      func.update(item.evento).then((value: Seminario | Simulado | Treinamento | SegurancaBarragem | RequestError) => {
        if ("error" in value) {
          setError(value);
          return;
        }

        if (file) {
          func.setFile(value.id, file).then((value: Seminario | Simulado | Treinamento | SegurancaBarragem | RequestError) => {
            if ("error" in value) {
              setError(value);
              return;
            }
            setState(!state);
          });
        }
        else {
          setState(!state);
        }
      });
    } else {
      func.add(barragem.id, item.evento).then((value: Seminario | Simulado | Treinamento | SegurancaBarragem | RequestError) => {
        if ("error" in value) {
          setError(value);
          return;
        }

        if (file) {
          func.setFile(value.id, file).then((value: Seminario | Simulado | Treinamento | SegurancaBarragem | RequestError) => {
            if ("error" in value) {
              setError(value);
              return;
            }
            setState(!state);
          });
        }
        else {
          setState(!state);
        }
      });
    }

    setAddDate(undefined);
    setEditItem(undefined);
    setExcludeItem(undefined);
  };

  return <>
    {/* Error */}
    <ErrorOnDialog
      open={Boolean(error)}
      error={error?.error ?? ""}
      message={error?.message ?? ""}
      onClose={() => setError(undefined)}
    />

    {/* Excluir item */}
    {user && !('error' in user) && user.admin && excludeItem &&
      <ConfirmModal
        open={Boolean(excludeItem)}
        title="Deseja mover este item para a lixeira?"
        message="Isto irá mover o item e todos os dados dele para a lixeira. Mas não se preocupe, você pode restaurar estes dados a qualquer momento em 'Lixeira'"
        onCancel={() => setExcludeItem(undefined)}
        onConfirm={handleExclude}
      />
    }

    {/* Popup Add or Change*/}
    {user && !('error' in user) && user.admin && addDate &&
      <Dialog maxWidth={"xl"} open={Boolean(addDate)} onClose={() => setAddDate(undefined)}>
        <CardNovoEvento
          data={addDate}
          eventChange={editItem}
          onAdd={handleAdd}
          onClose={() => setAddDate(undefined)}
        />
      </Dialog>
    }

    {/* Calendário */}
    <Box sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
    }}>

      {/* Dias da semana */}
      <Box sx={{
        pl: "1em",
        pr: "0.5em",
        display: "flex",
        alignItems: "center",
        borderRadius: "1em 1em 0 0",
        minHeight: minHeight ?? "1.5em",
        backgroundColor: 'secondary.main',
      }}>
        <div style={{ display: "flex", flex: 1 }}>
          <DiaSemana dia="Mês" />
          <DiaSemana dia="D" />
          <DiaSemana dia="S" />
          <DiaSemana dia="T" />
          <DiaSemana dia="Q" />
          <DiaSemana dia="Q" />
          <DiaSemana dia="S" />
          <DiaSemana dia="S" />
        </div>

        {/* Opções */}
        <div style={{ width: "1em", display: "flex", justifyContent: 'left' }}>
          {indexSemana !== 0 &&
            <Tooltip title="Voltar para a a data atual">
              <IconButton
                sx={{
                  padding: 0.5,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1rem'
                  }
                }}
                onClick={() => setIndexSemana(0)} size="small"
              >
                <CalendarToday fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        </div>
      </Box>

      {/* Calendário */}
      <Box
        sx={{
          flex: 1,
          pl: "1em",
          pr: "0em",
          gap: "0.5em",
          width: "100%",
          height: "100%",
          display: "flex",
          borderRadius: "0 0 1em 1em",
          backgroundColor: palette.primary.main,
        }}>

        {/* Dias */}
        <Box sx={{
          flex: 1,
          p: "0.3em",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}>
          {Array.from({ length: days.length / 8 }, (_, i) => {
            const iStart = i * 8;
            return <div key={i} style={{
              flex: 1,
              gap: "0.5em",
              display: "flex",
              flexDirection: "row",
            }}>
              {days.slice(iStart, iStart + 8).map((item, j) => <DiaMes
                key={`${iStart}${j}`}
                {...item}
                onClick={handleAddEvent}
                onRemove={handleRemove}
                onEdit={handleChangeEvent}
              />)}
            </div>;
          })}
        </Box>

        {/* Opções */}
        <Box sx={{
          p: 0,
          width: "1em",
          display: "flex",
          flexDirection: "column",
        }}>
          <IconButton
            sx={{
              padding: 0.5, // Reduz o padding interno
              '& .MuiSvgIcon-root': {
                fontSize: '1rem' // Tamanho do ícone
              }
            }}
            color="secondary"
            onClick={() => setIndexSemana(indexSemana - 1)}
          >
            <KeyboardArrowUp />
          </IconButton>

          <div style={{ flex: 1 }} />

          <IconButton
            sx={{
              padding: 0.5, // Reduz o padding interno
              '& .MuiSvgIcon-root': {
                fontSize: '1rem' // Tamanho do ícone
              }
            }}
            color="secondary"
            onClick={() => setIndexSemana(indexSemana + 1)}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Box>

      </Box>

      {/* Legenda */}
      <Box
        ref={sizeRef}
        sx={{
          gap: "0.2em",
          width: "100%",
          height: "5em",
          display: "flex",
          padding: "0.5em",
          flexDirection: "column",
        }}>
        <Typography fontWeight={650} fontSize={"0.8em"} >Eventos</Typography>
        <div style={{ gap: "0.5em", display: "flex", flexDirection: "row" }}>

          <Box sx={{
            display: "flex",
            flexDirection: "column",
          }}>
            <ItemLegenda
              text={"Simulados de Emergência"}
              color={palette.calendario.simuladoColor}
            />
            <ItemLegenda
              text={"Treinamentos"}
              color={palette.calendario.treinamentoColor}
            />
          </Box>

          <div style={{ flex: 1 }} />

          <Box sx={{
            display: "flex",
            flexDirection: "column",
          }}>
            <ItemLegenda
              text={"Inspeções Técnicas"}
              color={palette.calendario.inspecaoColor}
            />
            <ItemLegenda
              text={"Seminários Orientativos"}
              color={palette.calendario.seminarioColor}
            />
          </Box>

          <div style={{ flex: 1 }} />

          <ItemLegenda
            text={"Em outra barragem"}
            color={palette.calendario.outraBarragemColor}
          />

        </div>

      </Box>

    </Box>
  </>;
};

const DiaSemana = ({ dia }: { dia: string; }) => {
  const { palette } = useTheme();
  return <div style={{
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "2px",
  }}>
    <Typography fontWeight={450} fontSize="0.8em" color={palette.background.default}>{dia}</Typography>
  </div>;
};

const ItemLegenda = ({ color, text }: { color: string; text: string; }) => {
  return <Box sx={{
    gap: "0.5em",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  }}>
    <Circle htmlColor={color} fontSize="small" />
    <Typography fontSize={"0.8em"} >{text}</Typography>
  </Box>;
};

function getWeekRange(dataQualquer: Date): Date[] {
  const data = new Date(dataQualquer);
  const diaSemana = data.getDay();

  const domingo = new Date(data);
  domingo.setDate(data.getDate() - diaSemana);
  domingo.setHours(0, 0, 0, 0);

  const semana: Date[] = [new Date(domingo)];

  for (let i = 1; i < 7; i++) {
    const dia = new Date(domingo);
    dia.setDate(domingo.getDate() + i);
    semana.push(dia);
  }

  return semana;
}

function processWeek(dia: Date, now: Date) {
  const daysShow: DiaMesProps[] = [];

  const semana = getWeekRange(dia);
  const contadorMeses: Record<number, number> = {};

  for (const data of semana) {
    const mes = data.getMonth();
    contadorMeses[mes] = (contadorMeses[mes] || 0) + 1;

    daysShow.push({
      data,
      simulados: [],
      seminarios: [],
      segBarragem: [],
      treinamentos: [],
      isToday: data.getTime() === now.getTime(),
    });
  }

  let mesMaisFrequente = 0;

  let maiorContagem = 0;
  for (const mes in contadorMeses) {
    if (contadorMeses[mes] > maiorContagem) {
      maiorContagem = contadorMeses[mes];
      mesMaisFrequente = parseInt(mes);
    }
  }

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  daysShow.unshift({
    data: meses[mesMaisFrequente],
    simulados: [],
    seminarios: [],
    treinamentos: [],
    segBarragem: [],
  });

  return daysShow;
}